import { DriversMetrics } from "@/hooks/useDashboardApi";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import DashboardCard from "@/components/dashboard-card";
import TimeWidget from "@/components/time-widget";
import DonutChart from "@/components/donut-chart";
import { DriversLineChart } from "@/components/line-chart";

interface DriversDashboardProps {
  driversMetrics: DriversMetrics | null;
  loading: boolean;
  period: "day" | "week" | "month";
}

const DriversDashboard = ({
  driversMetrics,
  loading,
  period,
}: DriversDashboardProps) => {
  // Helper function to safely get worker type count
  const getWorkerTypeCount = (
    workerType: "full-time" | "part-time" | "freelance",
  ): number => {
    if (period === "day") {
      return (
        driversMetrics?.snapshot_drivers?.active_worker_types?.[workerType] || 0
      );
    }
    return driversMetrics?.active_worker_types?.[workerType] || 0;
  };

  // Helper function to safely get driver count
  const getDriverCount = (type: "active" | "inactive"): number => {
    if (period === "day") {
      return type === "active"
        ? driversMetrics?.snapshot_drivers?.active_drivers || 0
        : driversMetrics?.snapshot_drivers?.inactive_drivers || 0;
    }
    return type === "active"
      ? driversMetrics?.active_drivers || 0
      : driversMetrics?.inactive_drivers || 0;
  };

  // Donut chart data (example: by type)
  const donutData = [
    {
      name: "Tiempo completo",
      value: getWorkerTypeCount("full-time"),
      color: "#3b82f6",
    },
    {
      name: "Medio tiempo",
      value: getWorkerTypeCount("part-time"),
      color: "#06b6d4",
    },
    {
      name: "Freelance",
      value: getWorkerTypeCount("freelance"),
      color: "#8b5cf6",
    },
  ];

  // Line chart data for trips per driver average
  const lineData =
    driversMetrics?.trips_per_driver_average?.sort((a, b) =>
      a.date.localeCompare(b.date),
    ) || [];

  // Early return if no data to prevent crashes
  if (!driversMetrics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No hay datos disponibles</p>
      </div>
    );
  }

  // Memoized loading state - only show loading when there's no existing data

  if (loading) {
    return (
      <div className="text-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Formatea los horarios a un formato legible, segundos con toFixed(2)
  // total_hours_worked y average_road_time_minutes pueden ser decimales, así que calculamos horas, minutos y segundos reales

  function formatTimeFromMinutes(totalMinutes: number) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    const seconds = Number(
      ((totalMinutes - Math.floor(totalMinutes)) * 60).toFixed(2),
    );
    return { hours, minutes, seconds };
  }

  const totalHoursWorked = formatTimeFromMinutes(
    driversMetrics?.total_hours_worked || 0,
  );
  const averageRoadTime = formatTimeFromMinutes(
    driversMetrics?.average_road_time_minutes || 0,
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Driver Metrics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="h-24">
          <DashboardCard
            value={driversMetrics?.total_drivers || 0}
            title="Total"
            showIndicator={false}
          />
        </div>
        <div className="h-24">
          <DashboardCard
            value={getDriverCount("active")}
            title={"Activos"}
            showIndicator={false}
          />
        </div>
        <div className="h-24">
          <DashboardCard
            value={getDriverCount("inactive")}
            title={"Inactivos"}
            showIndicator={false}
          />
        </div>
        <div className="h-24">
          <DashboardCard
            value={driversMetrics?.total_orders || 0}
            title="Pedidos totales"
            showIndicator={false}
          />
        </div>
      </div>

      {/* Time & Distance Metrics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="h-24 lg:h-32">
          <DashboardCard
            value={driversMetrics?.total_completed_orders || 0}
            title="Pedidos completados"
            showIndicator={false}
          />
        </div>
        <div className="h-24 lg:h-32">
          <DashboardCard
            value={`${driversMetrics?.overall_completion_rate_percentage || 0}%`}
            title="Porcentaje de completados"
            showIndicator={false}
          />
        </div>
        <div className="h-24 md:h-32">
          <TimeWidget
            hours={totalHoursWorked.hours}
            minutes={totalHoursWorked.minutes}
            seconds={Number(totalHoursWorked.seconds.toFixed(2))}
            title="Horas trabajadas"
          />
        </div>
        <div className="h-24 md:h-32">
          <TimeWidget
            hours={averageRoadTime.hours}
            minutes={averageRoadTime.minutes}
            seconds={Number(averageRoadTime.seconds.toFixed(2))}
            title="Tiempo promedio en ruta"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="h-full">
          <DonutChart
            data={donutData}
            title={"Tipo de conductores activos"}
            totalLabel="conductores"
            total={
              getWorkerTypeCount("full-time") +
              getWorkerTypeCount("part-time") +
              getWorkerTypeCount("freelance")
            }
          />
        </div>
        <div className="h-full">
          <DriversLineChart
            data={lineData}
            title="Viajes por conductor"
            isDualLine={true}
          />
        </div>
      </div>
    </div>
  );
};

export default DriversDashboard;
